import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  METRIC,
  RESPONSE,
  PROFILE_GROUP,
  PROFILE_METRIC,
  PROFILE_RESPONSE,
  TRENDS_METRIC,
  SCORE_TYPE,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Reputation module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 *
 * Note: Reputation is unique in that it:
 *   - Exposes the SCORE_TYPE URL key in all modes (multi-method module)
 *   - Has `defaultBase: "FAMILIAR"` (USER, ADRECALL, AWARE, GENPOP are disabled)
 *   - By Segment POINT_IN_TIME has both METRIC/RESPONSE AND PROFILE_GROUP/PROFILE_METRIC/PROFILE_RESPONSE
 *   - By Brand POINT_IN_TIME additionally includes TRENDS_METRIC and RESPONSE
 */

export const reputationModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    module: "REPUTATION",
    supportMarkets: true,
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    maximumAllowedMarkets: 25,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    // Reputation brands are further narrowed to only those whose reputationMarketIds
    // overlap with the selected markets — mirrors useReputationPayloadOverride.ts logic.
    // Applied inside buildActiveGroupSnapshot to produce a reputation-aware
    // marketFilteredBrandIds pool used by pruneInvalidFields for URL brand validation.
    isBrandsFilteredByReputationMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportBase: true,
    defaultBase: "FAMILIAR",
    supportedBases: ["FAMILIAR"],
    supportDatePeriod: true,
    // Reputation data is only available from 2025-11-17 onwards.
    dateBounds: {
      min: "2025-11-17",
      max: null,
      warningMessage:
        "Data for the previous time period comparison is only available from November 17, 2025.",
    },
    postDateBounds: {
      min: "2025-11-17",
      max: null,
      warningMessage:
        "Data for the previous time period comparison is only available from November 17, 2025.",
    },
    // Reputation uniquely exposes score-type selection
    // Send scoreType inside metricModules in the unified API payload
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["DONUT", "BAR", "DISTRIBUTION", "TABLE"],
    defaultVisualisationType: "DONUT",
    statTestingConfig: [
      { id: "POP", enabled: true, activeLabel: "Period-P" },
      {
        id: "YOY",
        enabled: true,
        activeLabel: "Year-Y",
        disableWhenDaysExceed: {
          days: 365,
          message:
            "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
        },
        disableForPeriods: {
          periods: ["P_W"],
          message:
            "Year-over-Year comparison is only available for Months & Quarters.",
        },
      },
      { id: "PTP", hidden: true },
    ],
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        maximumAllowedBrands: 1,
        apiGroupedBy: "SEGMENT",
        apiSendFilters: false,
        // Routes: [TREND, POINT_IN_TIME, DISTRIBUTION] — no PRE_POST
        supportedModes: ["TREND", "POINT_IN_TIME"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              VIEW_ID,
              TRENDS_METRIC,
              RESPONSE,
              SCORE_TYPE,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "reputation",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            // Reputation By Segment POINT_IN_TIME has METRIC/RESPONSE AND PROFILE_GROUP keys
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              METRIC,
              RESPONSE,
              SCORE_TYPE,
              PROFILE_GROUP,
              PROFILE_METRIC,
              PROFILE_RESPONSE,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DISTRIBUTION: {
              config: {
                supportBase: false,
                dateBounds: {
                  bySubKey: { demographics: { min: null, max: null } },
                },
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  VIEW_ID,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  SCORE_TYPE,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "BRAND",
        apiSendFilters: true,
        // Routes: [TREND, POINT_IN_TIME, PRE_POST, DISTRIBUTION]
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              RESPONSE,
              SCORE_TYPE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "reputation",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            // Reputation By Brand POINT_IN_TIME additionally includes TRENDS_METRIC and RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              METRIC,
              RESPONSE,
              SCORE_TYPE,
              PROFILE_GROUP,
              PROFILE_METRIC,
              PROFILE_RESPONSE,
              TRENDS_METRIC,
              VIEW_ID,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DISTRIBUTION: {
              config: {
                supportBase: false,
                dateBounds: {
                  bySubKey: { demographics: { min: null, max: null } },
                },
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  SCORE_TYPE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            maximumAllowedBrands: 1,
            supportedVisualisationTypes: ["BAR", "TABLE"],
            defaultVisualisationType: "BAR",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VISUALIZATION_TYPE,
              SCORE_TYPE,
              VIEW_ID,
            ],
            statTestingConfig: [
              { id: "POP", enabled: true, activeLabel: "Period-P" },
              {
                id: "YOY",
                enabled: false,
                activeLabel: "Year-Y",
                disabledMessage:
                  "Year-over-Year comparison is not available on this screen.",
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: true,
                enabled: false,
                activeLabel: "Peer-P",
                fallbackTo: "POP",
              },
            ],
          },
        },
      },
    },

    // ── By Market ────────────────────────────────────────────────────────────
    MARKET: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        isMarketsFilteredByBrand: true,
        isBrandsFilteredByMarket: false,
        // By Market: brand is the primary dimension; markets are derived from the brand.
        // There are no pre-selected markets to check against, so we cannot use
        // isBrandsFilteredByReputationMarket (which checks against selectedMarketIds).
        // Instead, isBrandsFilteredByOwnReputationMarkets filters brands whose
        // reputationMarketIds overlaps with their own marketIds — ensuring only brands
        // that have reputation data for at least one of their own markets are eligible.
        // Override the module-level isBrandsFilteredByReputationMarket: true.
        isBrandsFilteredByReputationMarket: false,
        isBrandsFilteredByOwnReputationMarkets: true,
        maximumAllowedBrands: 1,
        apiGroupedBy: "MARKET",
        apiSendFilters: true,
        // Routes: [TREND, POINT_IN_TIME, DISTRIBUTION] — no PRE_POST
        supportedModes: ["TREND", "POINT_IN_TIME"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              RESPONSE,
              SCORE_TYPE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "reputation",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              METRIC,
              RESPONSE,
              SCORE_TYPE,
              PROFILE_GROUP,
              PROFILE_METRIC,
              PROFILE_RESPONSE,
              VIEW_ID,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DISTRIBUTION: {
              config: {
                supportBase: false,
                dateBounds: {
                  bySubKey: { demographics: { min: null, max: null } },
                },
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  SCORE_TYPE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
      },
    },
  },
};
